import React, {
  useState,
  useContext,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import useSwr from '@hooks/useSwr';
import { Radio, Typography, Skeleton, message, Button, Tooltip } from 'antd';
import {
  UsergroupAddOutlined,
  UsergroupDeleteOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import Wizard from '@components/Wizard';
import { ICustomer } from '@pages/Access/types';
import SuperList from '@components/SuperList';
import PermissionsCard, {
  GroupedRolesProps,
} from '@components/PermissionsCard';
import api from '@services/api';
import SessionContext from '@providers/Session/SessionContext';
import rolesConstants from '@utils/rolesConstants';
import ActionTypes from '@utils/deviceActions';
import { useNavigate } from 'react-router-dom';
import { ApplicationsConstants } from '@utils/applicationsConstants';
import * as S from './styles';

const { Paragraph } = Typography;

const ROOT_ID = 999999;

interface IBatchPermissionsModalProps {
  selectedCustomers?: ICustomer[];
  isMotBusiness: boolean;
}

interface ICustomerItem {
  label: string;
  value: number;
}

const BatchPermissionsModal: React.FC<IBatchPermissionsModalProps> = ({
  selectedCustomers,
  isMotBusiness,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('customers');

  const { keycloak } = useContext(KeycloakContext);
  const {
    isRoot,
    selectedApp,
    customer: { customersData },
  } = useContext(SessionContext);

  const customerIdLogged = keycloak.idTokenParsed?.customerId;
  const customerTypeLogged = keycloak.idTokenParsed?.customerType;

  const [preSelectedCustomers, setPreSelectedCustomers] = useState<
    ICustomerItem[] | undefined
  >([]);
  const [allSelectedCustomers, setAllSelectedCustomers] = useState<
    ICustomerItem[]
  >([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>();
  const [action, setAction] = useState();
  const [showModal, setShowModal] = useState(false);

  const { data: availableRoles } = useSwr(
    `/customers/${customerIdLogged}/roles/`,
    {
      params: {
        customerType: customerTypeLogged,
      },
    },
  );

  const clearStates = useCallback(() => {
    setPreSelectedCustomers([]);
    setAllSelectedCustomers([]);
    setSelectedRoles([]);
    setAction(undefined);
    setShowModal(false);
  }, []);

  const onFinish = useCallback(async () => {
    const actionType =
      action === 'add-permission'
        ? ActionTypes.ADDING_PERMISSIONS
        : ActionTypes.REMOVAL_PERMISSIONS;

    try {
      const customerIds = allSelectedCustomers?.map(({ value }) => value);

      const payload = {
        action: actionType,
        permissions: {
          roles: selectedRoles,
          customerIds,
        },
      };

      await api.post('/service-proxy/actions', payload);

      message.success(
        <>
          {t('batch-permissions.msgs.success-inserting-action')}
          <Button
            type="link"
            size="small"
            onClick={() => {
              let baseUrl = '/multi_connect';
              if (selectedApp !== ApplicationsConstants.ACCESS) {
                baseUrl = `/${selectedApp}/mobile-network`;
              }
              baseUrl += `/operational/actions-queue?action=${actionType}`;
              navigate(baseUrl);
            }}
          >
            {t('batch-permissions.msgs.success-inserting-action-link')}
          </Button>
        </>,
      );

      clearStates();
    } catch {
      message.error(t('batch-permissions.msgs.error-inserting-action'));
    }
  }, [
    action,
    allSelectedCustomers,
    clearStates,
    navigate,
    selectedApp,
    selectedRoles,
    t,
  ]);

  const prepareDataForSuperList = useCallback(
    (list: ICustomer[] | undefined) =>
      list?.map(
        ({
          id,
          name,
          cpfCnpj,
        }: {
          id: number;
          name: string;
          cpfCnpj: string;
        }) => ({
          label: name,
          value: id,
          cpfCnpj,
        }),
      ),
    [],
  );

  const superListItems = useMemo(
    () =>
      prepareDataForSuperList(customersData)?.filter(
        ({ value }) => value !== ROOT_ID,
      ),
    [prepareDataForSuperList, customersData],
  );

  const superListPreSelectedItems = useMemo(
    () =>
      prepareDataForSuperList(selectedCustomers)?.filter(
        ({ value }) => value !== ROOT_ID,
      ),
    [prepareDataForSuperList, selectedCustomers],
  );

  useEffect(() => {
    if (superListPreSelectedItems) {
      setPreSelectedCustomers(superListPreSelectedItems);
    }
  }, [superListPreSelectedItems]);

  const customerSelectionStep = (
    <div style={{ padding: '16px 8px' }}>
      <SuperList
        items={superListItems}
        preSelectedItems={preSelectedCustomers}
        columnName="cpfCnpj"
        onSelectedItemsChange={setAllSelectedCustomers}
        uploadAlternateTitle={t('attributes:cpfCnpj')}
      />
    </div>
  );

  const actionStep = (
    <S.AddOrRemoveRadioGroup
      value={action}
      onChange={({ target: { value } }) => setAction(value)}
    >
      <Radio.Button value="add-permission" className="add-or-remove-radio">
        <UsergroupAddOutlined />
        <p>{t('batch-permissions.action.addition')}</p>
      </Radio.Button>
      <Radio.Button
        value="remove-permission"
        className="add-or-remove-radio remove-radio"
      >
        <UsergroupDeleteOutlined />
        <p>{t('batch-permissions.action.removal')}</p>
      </Radio.Button>
    </S.AddOrRemoveRadioGroup>
  );

  const groupedData = useMemo(
    () =>
      availableRoles &&
      Object.entries(
        availableRoles.reduce(
          (groups: any, item: any) => ({
            ...groups,
            [item.category]: [...(groups[item.category] || []), item],
          }),
          {},
        ),
      ),
    [availableRoles],
  );

  const groupedRoles = useMemo(
    () =>
      groupedData?.map(([groupedCategory, permissions]: GroupedRolesProps) => [
        groupedCategory,
        permissions,
      ]),
    [groupedData],
  );

  const permissionsStep = (
    <Skeleton loading={!groupedRoles} active>
      <div style={{ padding: '16px 8px' }}>
        <PermissionsCard
          groupedRoles={groupedRoles}
          selectedRoles={selectedRoles}
          setSelectedRoles={setSelectedRoles}
          filterRoles={[
            {
              category: 'support',
              roles: [
                {
                  name: rolesConstants.VIEW_SERVICE_REQUESTS,
                  visible: isRoot,
                },
                {
                  name: rolesConstants.MANAGE_SERVICE_REQUESTS,
                  visible: isRoot,
                },
              ],
            },
          ]}
        />
      </div>
    </Skeleton>
  );

  const validationStep = useMemo(() => {
    const actionText = action === 'add-permission' ? 'added' : 'removed';

    return (
      <S.ValidationStepContainer isAdd={action === 'add-permission'}>
        <span className="validation-exclamation-circle">
          <ExclamationCircleOutlined />
        </span>
        <Paragraph className="validation-paragraph">
          {t('batch-permissions.validation-text.permissions', {
            count: selectedRoles?.length,
          })}
          {t(`batch-permissions.validation-text.${actionText}`, {
            count: selectedRoles?.length,
          })}
          {isMotBusiness
            ? t('batch-permissions.validation-text.departments', {
                count: allSelectedCustomers?.length,
              })
            : t('batch-permissions.validation-text.customers', {
                count: allSelectedCustomers?.length,
              })}
        </Paragraph>
      </S.ValidationStepContainer>
    );
  }, [
    action,
    allSelectedCustomers?.length,
    selectedRoles?.length,
    t,
    isMotBusiness,
  ]);

  return (
    <>
      <Tooltip title={t('batch-permissions.title')}>
        <Button
          icon={<UsergroupAddOutlined />}
          onClick={() => setShowModal(true)}
        />
      </Tooltip>
      <Wizard
        visibleState={[showModal, () => setShowModal(false)]}
        modalConfig={{
          maskClosable: false,
          style: { maxWidth: 680 },
          title: t(
            `batch-permissions.${action ? `action.${action}` : 'title'}`,
          ),
          width: '100%',
          centered: true,
          destroyOnClose: true,
        }}
        onFinish={onFinish}
        clearStates={clearStates}
        stepsConfig={{ size: 'small' }}
        arrSteps={[
          {
            stepConfig: {
              title: isMotBusiness ? t('departments') : t('customers'),
            },
            stepContent: customerSelectionStep,
            stepDisableNext:
              !allSelectedCustomers ||
              (allSelectedCustomers && allSelectedCustomers.length === 0),
            // @ts-ignore
            onNextStep: () => setPreSelectedCustomers(allSelectedCustomers),
          },
          {
            stepConfig: {
              title: t('batch-permissions.action.title'),
            },
            stepContent: actionStep,
            stepDisableNext: !action,
          },
          {
            stepConfig: {
              title: t('batch-permissions.permissions'),
            },
            stepContent: permissionsStep,
            stepDisableNext: !selectedRoles || selectedRoles?.length === 0,
          },
          {
            stepConfig: {
              title: t('batch-permissions.validation'),
            },
            stepContent: validationStep,
            stepDisableNext: false,
          },
        ]}
      />
    </>
  );
};

export default BatchPermissionsModal;
